import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ArticleCard from './components/ArticleCard';
import CategoryFilter from './components/CategoryFilter';
import AuthorProfile from './components/AuthorProfile';
import NewsletterSubscription from './components/NewsletterSubscription';
import TrendingTopics from './components/TrendingTopics';
import SearchBar from './components/SearchBar';
import RelatedArticles from './components/RelatedArticles';

const NewsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Mock data for articles
  const articles = [
  {
    id: 1,
    title: "The Future of European Fintech: REXFEL\'s 2024 Investment Thesis",
    slug: "future-european-fintech-2024-investment-thesis",
    excerpt: "Our comprehensive analysis of emerging fintech trends across Europe, highlighting key investment opportunities in digital banking, cryptocurrency infrastructure, and regulatory technology solutions.",
    content: `The European fintech landscape is experiencing unprecedented transformation as regulatory frameworks mature and consumer adoption accelerates. Our investment thesis for 2024 focuses on three critical areas: embedded finance solutions, cryptocurrency infrastructure development, and regulatory technology innovations.\n\nEmbedded finance represents the largest opportunity, with market projections indicating 400% growth over the next three years. Companies integrating financial services directly into their platforms are seeing remarkable user engagement and revenue diversification.\n\nCryptocurrency infrastructure remains a strategic priority, particularly in Germany where regulatory clarity has improved significantly. We're actively seeking partnerships with companies building institutional-grade custody solutions and DeFi protocols.\n\nRegulatory technology continues to evolve rapidly, creating opportunities for startups that can navigate complex compliance requirements while maintaining user experience excellence.`,
    category: "Investment Thesis",
    image: "https://images.unsplash.com/photo-1624002534843-065785c2571d",
    imageAlt: "Modern office building with glass facade reflecting blue sky, representing fintech innovation and corporate growth",
    author: {
      id: 1,
      name: "Dr. Marcus Weber",
      role: "Managing Partner",
      avatar: "https://images.unsplash.com/photo-1734456611474-13245d164868",
      avatarAlt: "Professional headshot of middle-aged German businessman with gray hair in navy suit",
      bio: "Leading REXFEL's investment strategy with 15+ years in European venture capital and fintech innovation.",
      articlesCount: 24,
      followers: 3200,
      socialLinks: [
      { platform: "LinkedIn", icon: "Linkedin" },
      { platform: "Twitter", icon: "Twitter" }]

    },
    publishedAt: "2024-10-15T09:00:00Z", views: "2.4k",
    readTime: 8,
    tags: ["fintech", "investment", "europe", "cryptocurrency"]
  },
  {
    id: 2,
    title: "Scaling SaaS in Germany: Lessons from Our Portfolio Companies", slug: "scaling-saas-germany-portfolio-lessons", excerpt: "Deep dive into the strategies that helped our portfolio companies achieve 300% growth in the German market, including localization tactics and regulatory compliance frameworks.",
    content: `German SaaS market presents unique opportunities and challenges that require specialized approaches. Our portfolio analysis reveals three critical success factors: cultural localization, compliance-first architecture, and strategic partnership development.\n\nCultural localization extends beyond language translation to include business process adaptation, customer service expectations, and sales methodology adjustments. German enterprises value detailed documentation, comprehensive security protocols, and long-term partnership commitments.\n\nCompliance-first architecture has become non-negotiable, with GDPR requirements serving as the baseline for data protection standards. Companies that integrate privacy by design principles from the beginning consistently outperform those retrofitting compliance measures.\n\nStrategic partnerships with established German enterprises accelerate market penetration significantly. Our portfolio companies leveraging these relationships achieve 40% faster customer acquisition compared to direct sales approaches.`,
    category: "Market Analysis", image: "https://images.unsplash.com/photo-1531537264351-c1952d1db1f5", imageAlt: "Modern German office space with employees collaborating around laptops and charts on wall displays",
    author: {
      id: 2,
      name: "Sarah Chen", role: "Investment Director", avatar: "https://images.unsplash.com/photo-1597621969117-1a305d3e0c68", avatarAlt: "Professional portrait of Asian woman with shoulder-length black hair in white blazer", bio: "Specializing in SaaS investments and European market expansion strategies with focus on B2B enterprise solutions.",
      articlesCount: 18,
      followers: 2800,
      socialLinks: [
      { platform: "LinkedIn", icon: "Linkedin" },
      { platform: "Medium", icon: "BookOpen" }]

    },
    publishedAt: "2024-10-12T14:30:00Z", views: "1.8k",
    readTime: 6,
    tags: ["saas", "germany", "scaling", "portfolio"]
  },
  {
    id: 3,
    title: "Cryptocurrency Regulation Update: What Hamburg Startups Need to Know", slug: "cryptocurrency-regulation-hamburg-startups", excerpt: "Latest regulatory developments in German cryptocurrency legislation and their impact on startup operations, funding strategies, and compliance requirements.",
    content: `Recent updates to German cryptocurrency regulations present both opportunities and challenges for Hamburg-based startups. The new framework provides clearer guidelines while introducing additional compliance requirements that startups must navigate carefully.\n\nKey regulatory changes include enhanced KYC requirements for cryptocurrency exchanges, updated tax reporting obligations for digital asset transactions, and new licensing requirements for custody service providers. These changes affect both crypto-native startups and traditional companies integrating blockchain technology.\n\nFunding implications are significant, with new regulations affecting how startups can raise capital through token offerings and how investors can participate in cryptocurrency-related investments. We've developed comprehensive compliance frameworks to help our portfolio companies navigate these requirements.\n\nOperational considerations include data protection requirements specific to blockchain applications, cross-border transaction reporting, and integration with traditional banking systems. Startups that proactively address these requirements position themselves for sustainable growth in the evolving regulatory landscape.`,
    category: "Regulatory Update", image: "https://images.unsplash.com/photo-1586089080640-7798a9449d5c", imageAlt: "Hamburg city skyline with modern financial district buildings and Elbe river in foreground",
    author: {
      id: 3,
      name: "Thomas Müller", role: "Legal & Compliance Lead", avatar: "https://images.unsplash.com/photo-1585066047759-3438c34cf676", avatarAlt: "Professional headshot of German man with brown hair and beard wearing dark suit", bio: "Expert in cryptocurrency regulation and compliance frameworks with extensive experience in German financial law.",
      articlesCount: 12,
      followers: 1900,
      socialLinks: [
      { platform: "LinkedIn", icon: "Linkedin" }]

    },
    publishedAt: "2024-10-10T11:15:00Z", views: "3.1k",
    readTime: 7,
    tags: ["cryptocurrency", "regulation", "compliance", "hamburg"]
  },
  {
    id: 4,
    title: "E-commerce Innovation: How Mobellex Revolutionized Furniture Retail", slug: "ecommerce-innovation-mobellex-furniture-retail", excerpt: "Case study examining Mobellex's journey from startup to market leader, highlighting innovative approaches to online furniture retail and customer experience optimization.", content: `Mobellex's transformation of the furniture retail landscape demonstrates the power of technology-driven customer experience innovation. Their journey from a Hamburg-based startup to European market leader provides valuable insights for e-commerce entrepreneurs.\n\nThe company's success stems from three core innovations: augmented reality furniture visualization, AI-powered interior design recommendations, and sustainable supply chain optimization. These technologies address traditional furniture retail pain points while creating new value propositions for customers.\n\nAugmented reality implementation allows customers to visualize furniture in their actual living spaces, reducing return rates by 60% and increasing customer satisfaction scores significantly. The technology integration required substantial investment but delivered measurable ROI within 18 months.\n\nAI-powered recommendations analyze customer preferences, room dimensions, and style preferences to suggest complementary products and complete room designs. This approach increased average order values by 45% while improving customer engagement metrics across all digital touchpoints.\n\nSustainable supply chain practices, including local sourcing partnerships and carbon-neutral delivery options, resonate strongly with European consumers and differentiate Mobellex from traditional furniture retailers.`,
    category: "Portfolio Spotlight",
    image: "https://images.unsplash.com/photo-1608062372082-1c657bf87e50",
    imageAlt: "Modern furniture showroom with sleek sofas and tables displayed in bright, minimalist setting",
    author: {
      id: 4,
      name: "Elena Rodriguez",
      role: "Portfolio Manager",
      avatar: "https://images.unsplash.com/photo-1721294928128-f6c2d1d2281d",
      avatarAlt: "Professional portrait of Hispanic woman with long dark hair in burgundy blazer",
      bio: "Managing REXFEL\'s e-commerce and retail technology investments with focus on customer experience innovation.",
      articlesCount: 15,
      followers: 2100,
      socialLinks: [
      { platform: "LinkedIn", icon: "Linkedin" },
      { platform: "Twitter", icon: "Twitter" }]

    },
    publishedAt: "2024-10-08T16:45:00Z",
    views: "2.7k",
    readTime: 9,
    tags: ["ecommerce", "innovation", "portfolio", "retail"]
  },
  {
    id: 5,
    title: "Digital Transformation Playbook: Enterprise Adoption Strategies",
    slug: "digital-transformation-enterprise-adoption-strategies",
    excerpt: "Comprehensive guide to enterprise digital transformation, featuring proven methodologies and implementation frameworks developed through our portfolio company experiences.",
    content: `Enterprise digital transformation requires systematic approaches that balance innovation with operational stability. Our playbook, developed through extensive portfolio company collaboration, provides actionable frameworks for successful transformation initiatives.\n\nPhase-based implementation strategies minimize disruption while maximizing adoption rates. We recommend starting with pilot programs in non-critical business areas, allowing teams to develop expertise and confidence before expanding to core operations.\n\nChange management protocols address the human element of transformation, which often determines project success more than technical capabilities. Effective communication, training programs, and incentive alignment ensure employee buy-in throughout the transformation process.\n\nTechnology stack decisions should prioritize integration capabilities and scalability over feature richness. Companies that select platforms based on long-term architectural vision consistently achieve better outcomes than those focusing on immediate functionality needs.\n\nMeasurement frameworks must include both quantitative metrics and qualitative assessments. ROI calculations should account for productivity improvements, customer satisfaction gains, and competitive positioning advantages beyond direct cost savings.`,
    category: "Educational Resource",
    image: "https://images.unsplash.com/photo-1663780852957-0e1f8bda3d0d",
    imageAlt: "Business team analyzing digital transformation charts and graphs on large wall-mounted displays",
    author: {
      id: 5,
      name: "Dr. Andreas Schmidt",
      role: "Technology Advisor",
      avatar: "https://images.unsplash.com/photo-1660847507502-747d97f3bd08",
      avatarAlt: "Professional headshot of German technology executive with glasses and gray beard in navy jacket",
      bio: "Technology strategy consultant with 20+ years experience in enterprise digital transformation and startup scaling.",
      articlesCount: 21,
      followers: 4100,
      socialLinks: [
      { platform: "LinkedIn", icon: "Linkedin" },
      { platform: "Medium", icon: "BookOpen" }]

    },
    publishedAt: "2024-10-05T13:20:00Z",
    views: "1.9k",
    readTime: 11,
    tags: ["digital-transformation", "enterprise", "strategy", "technology"]
  },
  {
    id: 6,
    title: "Startup Ecosystem Hamburg: Q3 2024 Investment Report",
    slug: "hamburg-startup-ecosystem-q3-2024-report",
    excerpt: "Quarterly analysis of Hamburg's startup landscape, including funding trends, sector performance, and emerging opportunities in Northern Germany's innovation hub.", content: `Hamburg's startup ecosystem demonstrated remarkable resilience and growth throughout Q3 2024, with total funding reaching €180 million across 45 deals. The city continues to establish itself as Northern Germany's premier innovation hub, attracting both domestic and international investment.\n\nSector analysis reveals strong performance in maritime technology, logistics innovation, and sustainable energy solutions. These areas align with Hamburg's traditional industrial strengths while embracing digital transformation opportunities.\n\nFunding trends show increased investor confidence in early-stage companies, with seed and Series A rounds comprising 70% of total deal volume. This indicates a healthy pipeline of emerging companies and sustained investor appetite for Hamburg-based startups.\n\nInternational investment activity increased by 35% compared to Q2, with particular interest from Scandinavian and UK-based funds. This trend reflects Hamburg's growing reputation as a gateway to European markets and its strategic location advantages.\n\nEmerging opportunities include climate technology solutions, digital health platforms, and B2B software targeting traditional industries. These sectors present significant potential for startups that can bridge technology innovation with established industry expertise.`,
    category: "Market Report",
    image: "https://images.unsplash.com/photo-1611865422861-391cf5f3ba49",
    imageAlt: "Hamburg port and city skyline with cranes and modern buildings reflecting startup ecosystem growth",
    author: {
      id: 6,
      name: "Lisa Andersson",
      role: "Market Research Analyst",
      avatar: "https://images.unsplash.com/photo-1647326164285-b882810647cb",
      avatarAlt: "Professional portrait of Scandinavian woman with blonde hair in light blue business attire",
      bio: "Analyzing European startup ecosystems and investment trends with specialization in Northern European markets.",
      articlesCount: 9,
      followers: 1600,
      socialLinks: [
      { platform: "LinkedIn", icon: "Linkedin" }]

    },
    publishedAt: "2024-10-03T10:00:00Z",
    views: "2.2k",
    readTime: 5,
    tags: ["hamburg", "startup-ecosystem", "investment-report", "q3-2024"]
  }];


  // Mock data for categories
  const categories = [
  { id: 'investment-thesis', name: 'Investment Thesis', icon: 'Target' },
  { id: 'market-analysis', name: 'Market Analysis', icon: 'TrendingUp' },
  { id: 'portfolio-spotlight', name: 'Portfolio Spotlight', icon: 'Star' },
  { id: 'regulatory-update', name: 'Regulatory Updates', icon: 'Scale' },
  { id: 'educational-resource', name: 'Educational Resources', icon: 'BookOpen' },
  { id: 'market-report', name: 'Market Reports', icon: 'BarChart3' }];


  // Mock data for trending topics
  const trendingTopics = [
  { id: 1, tag: 'fintech', count: 24, growth: 15 },
  { id: 2, tag: 'cryptocurrency', count: 18, growth: 28 },
  { id: 3, tag: 'saas', count: 16, growth: 12 },
  { id: 4, tag: 'ecommerce', count: 14, growth: 22 },
  { id: 5, tag: 'digital-transformation', count: 12, growth: 8 }];


  // Mock data for featured authors
  const featuredAuthors = [
  {
    id: 1,
    name: "Dr. Marcus Weber",
    role: "Managing Partner",
    avatar: "https://images.unsplash.com/photo-1734456611474-13245d164868",
    avatarAlt: "Professional headshot of middle-aged German businessman with gray hair in navy suit",
    bio: "Leading REXFEL's investment strategy with 15+ years in European venture capital and fintech innovation.",
    articlesCount: 24,
    followers: 3200,
    socialLinks: [
    { platform: "LinkedIn", icon: "Linkedin" },
    { platform: "Twitter", icon: "Twitter" }]

  },
  {
    id: 2,
    name: "Sarah Chen",
    role: "Investment Director",
    avatar: "https://images.unsplash.com/photo-1597621969117-1a305d3e0c68",
    avatarAlt: "Professional portrait of Asian woman with shoulder-length black hair in white blazer",
    bio: "Specializing in SaaS investments and European market expansion strategies with focus on B2B enterprise solutions.",
    articlesCount: 18,
    followers: 2800,
    socialLinks: [
    { platform: "LinkedIn", icon: "Linkedin" },
    { platform: "Medium", icon: "BookOpen" }]

  }];


  // Mock data for related articles
  const relatedArticles = [
  {
    id: 7,
    title: "AOGX Token Performance Analysis",
    slug: "aogx-token-performance-analysis",
    image: "https://images.unsplash.com/photo-1583373325529-501e03a3a8e7",
    imageAlt: "Cryptocurrency trading charts and graphs displayed on multiple computer monitors",
    publishedAt: "2024-10-01T15:30:00Z",
    readTime: 4,
    views: "1.5k"
  },
  {
    id: 8,
    title: "European Venture Capital Trends",
    slug: "european-venture-capital-trends",
    image: "https://images.unsplash.com/photo-1660422319578-f3148ee53bf5",
    imageAlt: "European business district with modern office buildings and financial institutions",
    publishedAt: "2024-09-28T12:00:00Z",
    readTime: 6,
    views: "2.1k"
  },
  {
    id: 9,
    title: "Sustainable Tech Investment Guide",
    slug: "sustainable-tech-investment-guide",
    image: "https://images.unsplash.com/photo-1728073395364-b7c8f9211750",
    imageAlt: "Wind turbines and solar panels in green landscape representing sustainable technology",
    publishedAt: "2024-09-25T09:45:00Z",
    readTime: 8,
    views: "1.8k"
  }];


  useEffect(() => {
    // Check for saved language preference
    const savedLanguage = localStorage.getItem('rexfel_language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    filterArticles();
  }, [activeCategory, searchTerm]);

  const filterArticles = () => {
    let filtered = articles;

    if (activeCategory !== 'all') {
      filtered = filtered?.filter((article) =>
      article?.category?.toLowerCase()?.replace(/\s+/g, '-') === activeCategory
      );
    }

    if (searchTerm) {
      filtered = filtered?.filter((article) =>
      article?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.excerpt?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.author?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.tags?.some((tag) => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      );
    }

    setFilteredArticles(filtered);
  };

  const getArticleCounts = () => {
    const counts = { all: articles?.length };
    categories?.forEach((category) => {
      counts[category.id] = articles?.filter((article) =>
      article?.category?.toLowerCase()?.replace(/\s+/g, '-') === category?.id
      )?.length;
    });
    return counts;
  };

  const handleTopicClick = (tag) => {
    setSearchTerm(tag);
    setActiveCategory('all');
  };

  const featuredArticle = filteredArticles?.[0];
  const regularArticles = filteredArticles?.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Market Intelligence & Investment Insights | REXFEL News</title>
        <meta name="description" content="Stay ahead with REXFEL's market intelligence center featuring investment analysis, startup ecosystem trends, and thought leadership from our expert team." />
        <meta name="keywords" content="investment insights, market analysis, startup news, fintech trends, venture capital, REXFEL" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 to-accent/5 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Market Intelligence
                <span className="text-gradient-brand block">& Investment Insights</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Stay ahead of market trends with expert analysis, investment thesis updates, and thought leadership from REXFEL's team of industry professionals.
              </p>
              
              <div className="max-w-2xl mx-auto">
                <SearchBar
                  onSearch={setSearchTerm}
                  placeholder="Search articles, topics, or authors..." />

              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">150+</div>
                <div className="text-sm text-muted-foreground">Published Articles</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">25k+</div>
                <div className="text-sm text-muted-foreground">Monthly Readers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">12</div>
                <div className="text-sm text-muted-foreground">Expert Authors</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">4</div>
                <div className="text-sm text-muted-foreground">Languages</div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {/* Category Filter */}
                <div className="mb-8">
                  <CategoryFilter
                    categories={categories}
                    activeCategory={activeCategory}
                    onCategoryChange={setActiveCategory}
                    articleCounts={getArticleCounts()} />

                </div>

                {/* Featured Article */}
                {featuredArticle &&
                <div className="mb-12">
                    <div className="flex items-center space-x-2 mb-6">
                      <Icon name="Star" size={20} color="var(--color-primary)" />
                      <h2 className="text-xl font-semibold">Featured Article</h2>
                    </div>
                    <ArticleCard article={featuredArticle} featured={true} />
                  </div>
                }

                {/* Articles Grid */}
                {regularArticles?.length > 0 ?
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {regularArticles?.map((article) =>
                  <ArticleCard key={article?.id} article={article} />
                  )}
                  </div> :

                <div className="text-center py-16">
                    <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="Search" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">No Articles Found</h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your search terms or category filters.
                    </p>
                    <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveCategory('all');
                    }}>

                      Clear Filters
                    </Button>
                  </div>
                }

                {/* Load More */}
                {regularArticles?.length > 0 &&
                <div className="text-center mt-12">
                    <Button variant="outline" size="lg">
                      <Icon name="ChevronDown" size={16} />
                      <span className="ml-2">Load More Articles</span>
                    </Button>
                  </div>
                }
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Newsletter Subscription */}
                <NewsletterSubscription />

                {/* Trending Topics */}
                <TrendingTopics
                  topics={trendingTopics}
                  onTopicClick={handleTopicClick} />


                {/* Featured Authors */}
                <div className="bg-card border border-border rounded-brand p-6">
                  <div className="flex items-center space-x-2 mb-6">
                    <Icon name="Users" size={20} color="var(--color-primary)" />
                    <h3 className="font-semibold">Featured Authors</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {featuredAuthors?.map((author) =>
                    <AuthorProfile key={author?.id} author={author} compact={true} />
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    className="mt-6">

                    <Icon name="Users" size={16} />
                    <span className="ml-2">View All Authors</span>
                  </Button>
                </div>

                {/* Related Articles */}
                <RelatedArticles articles={relatedArticles} />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-primary/10 to-accent/10 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Want Exclusive Investment Insights?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join our premium newsletter for in-depth market analysis, portfolio updates, and early access to investment opportunities.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90">

                <Icon name="Mail" size={20} />
                <span className="ml-2">Subscribe to Premium</span>
              </Button>
              
              <Button
                variant="outline"
                size="lg">

                <Icon name="Calendar" size={20} />
                <span className="ml-2">Schedule Consultation</span>
              </Button>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-brand-sm bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <span className="text-white font-bold text-lg font-mono">R</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gradient-brand">REXFEL</h3>
                  <p className="text-xs text-muted-foreground">Investment Platform</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-4">
                Strategic investment meets digital innovation. Building the future of European fintech and technology startups.
              </p>
              <div className="flex items-center space-x-4">
                <Button variant="outline" size="icon">
                  <Icon name="Linkedin" size={16} />
                </Button>
                <Button variant="outline" size="icon">
                  <Icon name="Twitter" size={16} />
                </Button>
                <Button variant="outline" size="icon">
                  <Icon name="Mail" size={16} />
                </Button>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="/about" className="block text-muted-foreground hover:text-primary transition-brand">About REXFEL</a>
                <a href="/portfolio" className="block text-muted-foreground hover:text-primary transition-brand">Portfolio</a>
                <a href="/crypto-dashboard" className="block text-muted-foreground hover:text-primary transition-brand">Crypto Dashboard</a>
                <a href="/contact" className="block text-muted-foreground hover:text-primary transition-brand">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <div className="space-y-2 text-sm">
                <a href="/legal-hub" className="block text-muted-foreground hover:text-primary transition-brand">Legal Hub</a>
                <a href="/innovation-lab" className="block text-muted-foreground hover:text-primary transition-brand">Innovation Lab</a>
                <a href="#" className="block text-muted-foreground hover:text-primary transition-brand">RSS Feed</a>
                <a href="#" className="block text-muted-foreground hover:text-primary transition-brand">Newsletter Archive</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date()?.getFullYear()} REXFEL Technology and Innovation. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default NewsPage;